import {
  useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ArrowUp, ExternalLink, GitBranch, Loader2, Sparkles, Wrench } from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { CosmoContext } from '@/hooks/useCosmoContext';

// ── Types ──────────────────────────────────────────────────────────────────────

interface ParsedLink { path: string; label: string }

interface CosmoMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  promptBuilderGoal?: string;
  workflowBuilderGoal?: string;
  links?: ParsedLink[];
  raw?: string;
}

export interface CosmoChatHandle {
  prefillAndFocus: (text: string) => void;
}

interface CosmoChatProps {
  ctx: CosmoContext;
  onOpenPromptBuilder: (goal: string) => void;
  onOpenWorkflowBuilder?: (goal: string) => void;
  onConversationStart?: () => void;
  mode: 'welcome' | 'conversation';
}

// ── Suggested starters ─────────────────────────────────────────────────────────

function getStarters(ctx: CosmoContext): string[] {
  const starters: string[] = [];
  if (ctx.isAdmin) {
    starters.push('How are my orgs doing?', "What needs attention?", "Draft a thinking session");
  }
  if (ctx.isCouncil && ctx.councilOrgs.length > 0) {
    starters.push(`Show me our momentum`, "What should we focus on?", "Prepare a team update");
  }
  if (!ctx.isAdmin && !ctx.isCouncil) {
    starters.push("Help me build a prompt", "What's trending in my org?", "I need to summarize a meeting");
  }
  return starters.slice(0, 4);
}

// ── Response parser ────────────────────────────────────────────────────────────

function parseAssistantResponse(raw: string): Omit<CosmoMessage, 'id' | 'role'> {
  let text = raw;

  let promptBuilderGoal: string | undefined;
  const triggerMatch = text.match(/\[TRIGGER:PROMPT_BUILDER:(.*?)\]/s);
  if (triggerMatch) {
    promptBuilderGoal = triggerMatch[1].trim();
    text = text.replace(/\[TRIGGER:PROMPT_BUILDER:.*?\]/s, '').trim();
  }

  let workflowBuilderGoal: string | undefined;
  const workflowTriggerMatch = text.match(/\[TRIGGER:WORKFLOW_BUILDER:(.*?)\]/s);
  if (workflowTriggerMatch) {
    workflowBuilderGoal = workflowTriggerMatch[1].trim();
    text = text.replace(/\[TRIGGER:WORKFLOW_BUILDER:.*?\]/s, '').trim();
  }

  const links: ParsedLink[] = [];
  const linkPattern = /\[LINK:(\/[^:]+):([^\]]+)\]/g;
  let match: RegExpExecArray | null;
  while ((match = linkPattern.exec(text)) !== null) {
    links.push({ path: match[1], label: match[2] });
  }
  text = text.replace(/\[LINK:\/[^:]+:[^\]]+\]/g, '').trim();

  return { content: text, promptBuilderGoal, workflowBuilderGoal, links: links.length > 0 ? links : undefined, raw };
}

// ── Message bubble ─────────────────────────────────────────────────────────────

function MessageBubble({
  message,
  onOpenPromptBuilder,
  onOpenWorkflowBuilder,
}: {
  message: CosmoMessage;
  onOpenPromptBuilder: (goal: string) => void;
  onOpenWorkflowBuilder?: (goal: string) => void;
}) {
  const navigate = useNavigate();
  const isUser = message.role === 'user';

  return (
    <div className={cn('flex gap-3', isUser ? 'justify-end' : 'justify-start')}>
      {!isUser && (
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 mt-0.5">
          <Sparkles className="h-4 w-4 text-primary" />
        </div>
      )}
      <div className={cn('max-w-[80%] space-y-2')}>
        <div
          className={cn(
            'rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap',
            isUser
              ? 'bg-primary text-primary-foreground'
              : 'text-foreground',
          )}
        >
          {message.content}
        </div>

        {message.promptBuilderGoal && (
          <Button
            size="sm"
            onClick={() => onOpenPromptBuilder(message.promptBuilderGoal!)}
            className="h-8 gap-1.5 text-xs"
            variant="outline"
          >
            <Wrench className="h-3.5 w-3.5" />
            Open Prompt Builder
          </Button>
        )}

        {message.workflowBuilderGoal && onOpenWorkflowBuilder && (
          <Button
            size="sm"
            onClick={() => onOpenWorkflowBuilder(message.workflowBuilderGoal!)}
            className="h-8 gap-1.5 text-xs"
            variant="outline"
          >
            <GitBranch className="h-3.5 w-3.5" />
            Build Workflow
          </Button>
        )}

        {message.links && message.links.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {message.links.map((link, i) => (
              <Button
                key={i}
                size="sm"
                variant="outline"
                className="h-7 text-xs gap-1"
                onClick={() => navigate(link.path)}
              >
                {link.label}
                <ExternalLink className="h-3 w-3" />
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Typing indicator ──────────────────────────────────────────────────────────

function TypingIndicator() {
  return (
    <div className="flex gap-3 justify-start">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
        <Sparkles className="h-4 w-4 text-primary" />
      </div>
      <div className="rounded-2xl px-4 py-3">
        <div className="flex gap-1.5 items-center">
          <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '0ms' }} />
          <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '150ms' }} />
          <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  );
}

// ── Input bar (shared between welcome + conversation modes) ───────────────────

function ChatInput({
  input,
  setInput,
  onSend,
  disabled,
  inputRef,
  placeholder,
}: {
  input: string;
  setInput: (v: string) => void;
  onSend: () => void;
  disabled: boolean;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  placeholder?: string;
}) {
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + 'px';
    }
  }, [input, inputRef]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  return (
    <div className="relative w-full">
      <div className="flex items-end rounded-2xl border border-border/50 bg-background shadow-sm transition-shadow focus-within:shadow-md focus-within:border-border/80">
        <textarea
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder ?? 'How can I help you today?'}
          rows={1}
          disabled={disabled}
          className={cn(
            'flex-1 resize-none bg-transparent px-5 py-4',
            'text-sm leading-relaxed placeholder:text-muted-foreground/50',
            'focus:outline-none',
            'disabled:opacity-50 min-h-[52px] max-h-[120px]',
          )}
        />
        <div className="px-3 pb-3">
          <Button
            size="icon"
            onClick={onSend}
            disabled={!input.trim() || disabled}
            className="h-9 w-9 rounded-xl transition-opacity"
          >
            {disabled && input.trim()
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <ArrowUp className="h-4 w-4" />
            }
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export const CosmoChat = forwardRef<CosmoChatHandle, CosmoChatProps>(
  function CosmoChat({ ctx, onOpenPromptBuilder, onOpenWorkflowBuilder, onConversationStart, mode }, ref) {
    const [messages, setMessages] = useState<CosmoMessage[]>([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null!);
    const starters = getStarters(ctx);

    useImperativeHandle(ref, () => ({
      prefillAndFocus: (text: string) => {
        setInput(text);
        setTimeout(() => {
          inputRef.current?.focus();
        }, 50);
      },
    }));

    const scrollToBottom = useCallback(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    useEffect(() => {
      scrollToBottom();
    }, [messages, isLoading, scrollToBottom]);

    const sendMessage = useCallback(async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isLoading || ctx.loading) return;

      setInput('');
      setError(null);

      // Signal parent to switch to conversation mode
      onConversationStart?.();

      const userMessage: CosmoMessage = {
        id: crypto.randomUUID(),
        role: 'user',
        content: trimmed,
      };
      setMessages(prev => [...prev, userMessage]);
      setIsLoading(true);

      try {
        const history = [...messages, userMessage].map(m => ({
          role: m.role as 'user' | 'assistant',
          content: m.raw ?? m.content,
        }));

        const { data, error: fnError } = await supabase.functions.invoke('cosmo-chat', {
          body: {
            messages: history,
            systemPrompt: ctx.systemPrompt || 'You are Cosmo, an AI partner.',
          },
        });

        if (fnError) throw fnError;

        const rawContent: string = data?.content ?? '';
        if (!rawContent) throw new Error('No response from Cosmo');

        const parsed = parseAssistantResponse(rawContent);
        const assistantMessage: CosmoMessage = {
          id: crypto.randomUUID(),
          role: 'assistant',
          ...parsed,
          raw: rawContent,
        };
        setMessages(prev => [...prev, assistantMessage]);

      } catch (err: any) {
        console.error('[CosmoChat] send error:', err);
        setError(err?.message ?? 'Something went wrong. Please try again.');
        setMessages(prev => prev.filter(m => m.id !== userMessage.id));
      } finally {
        setIsLoading(false);
      }
    }, [messages, isLoading, ctx.loading, ctx.systemPrompt, onConversationStart]);

    // ── Welcome mode: just the input box ──────────────────────────────────────
    if (mode === 'welcome') {
      return (
        <div className="w-full space-y-4">
          <ChatInput
            input={input}
            setInput={setInput}
            onSend={() => sendMessage(input)}
            disabled={isLoading || ctx.loading}
            inputRef={inputRef}
          />
          {/* Starter chips below input */}
          {starters.length > 0 && (
            <div className="flex flex-wrap justify-center gap-2">
              {starters.map((s, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(s)}
                  disabled={ctx.loading || isLoading}
                  className={cn(
                    'rounded-full border border-border/40 bg-background/60 px-4 py-2',
                    'text-sm text-muted-foreground transition-all duration-200',
                    'hover:border-primary/30 hover:text-foreground hover:shadow-sm',
                    'disabled:opacity-50',
                  )}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
      );
    }

    // ── Conversation mode: full chat thread ──────────────────────────────────
    return (
      <div className="flex h-full flex-col min-h-0">

        {/* Messages */}
        <ScrollArea className="flex-1 min-h-0">
          <div className="flex flex-col gap-6 py-8">
            {messages.map(msg => (
              <MessageBubble
                key={msg.id}
                message={msg}
                onOpenPromptBuilder={onOpenPromptBuilder}
                onOpenWorkflowBuilder={onOpenWorkflowBuilder}
              />
            ))}

            {isLoading && <TypingIndicator />}

            {error && (
              <div className="rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Input pinned to bottom */}
        <div className="shrink-0 py-4">
          <ChatInput
            input={input}
            setInput={setInput}
            onSend={() => sendMessage(input)}
            disabled={isLoading || ctx.loading}
            inputRef={inputRef}
            placeholder="Ask Cosmo anything…"
          />
        </div>
      </div>
    );
  }
);
